import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';
import API_BASE_URL from '../config/api';
import './LegalPages.css';

// Frontend base URL for viewing pages
const FRONTEND_BASE_URL = process.env.REACT_APP_FRONTEND_URL || 'http://localhost:3000';

const LegalPages = () => {
  const navigate = useNavigate();
  const [legalPages, setLegalPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [togglingStatus, setTogglingStatus] = useState({});

  useEffect(() => {
    fetchLegalPages();
  }, [searchTerm, filterStatus]);

  const fetchLegalPages = async () => {
    try {
      setLoading(true);
      const params = {
        ...(searchTerm && { search: searchTerm }),
        ...(filterStatus && { status: filterStatus })
      };
      const response = await axios.get(`${API_BASE_URL}/admin/legal-pages`, { params });
      setLegalPages(response.data.data || []);
      setLoading(false);
    } catch (error) {
      setError('Failed to fetch legal pages');
      setLoading(false);
    }
  };

  const handleToggleActive = async (pageId, currentStatus) => {
    const newStatus = currentStatus === 'active' ? 'inactive' : 'active';
    setTogglingStatus(prev => ({ ...prev, [pageId]: true }));
    
    try {
      const response = await axios.put(`${API_BASE_URL}/admin/legal-pages/${pageId}`, {
        status: newStatus
      });
      
      // Update the page in the local state
      setLegalPages(legalPages.map(page => 
        page._id === pageId 
          ? { ...page, status: response.data.data.status }
          : page
      ));
    } catch (error) {
      setError('Failed to update page status');
    } finally {
      setTogglingStatus(prev => ({ ...prev, [pageId]: false }));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this legal page?')) {
      return;
    }
    try {
      await axios.delete(`${API_BASE_URL}/admin/legal-pages/${id}`);
      fetchLegalPages();
    } catch (error) {
      setError('Failed to delete legal page');
    }
  };

  const formatDate = (date) => {
    if (!date) return '—';
    return new Date(date).toLocaleDateString();
  };

  const handleViewPage = (slug) => {
    const url = `${FRONTEND_BASE_URL}/page/${slug}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  if (loading && legalPages.length === 0) {
    return (
      <Layout>
        <div className="container">
          <div className="legal-pages-header">
            <h1>Legal Pages</h1>
            <div className="skeleton skeleton-button" style={{ width: '180px', height: '40px', borderRadius: '8px' }}></div>
          </div>
          <div className="legal-pages-filters">
            <div className="skeleton skeleton-text" style={{ width: '100%', height: '44px', borderRadius: '10px' }}></div>
            <div className="skeleton skeleton-text" style={{ width: '200px', height: '44px', borderRadius: '10px' }}></div>
          </div>
          <div className="legal-pages-table-container">
            <table className="legal-pages-table">
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Slug</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th>Updated</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {[1, 2, 3, 4, 5, 6].map((index) => (
                  <tr key={index} className="skeleton-row">
                    <td>
                      <div className="skeleton skeleton-text" style={{ width: '70%', height: '16px' }}></div>
                    </td>
                    <td>
                      <div className="skeleton skeleton-text" style={{ width: '60%', height: '14px' }}></div>
                    </td>
                    <td>
                      <div className="skeleton skeleton-switch" style={{ width: '100px', height: '26px' }}></div>
                    </td>
                    <td>
                      <div className="skeleton skeleton-text" style={{ width: '100px', height: '14px' }}></div>
                    </td>
                    <td>
                      <div className="skeleton skeleton-text" style={{ width: '100px', height: '14px' }}></div>
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <div className="skeleton skeleton-button" style={{ width: '36px', height: '36px', borderRadius: '8px' }}></div>
                        <div className="skeleton skeleton-button" style={{ width: '36px', height: '36px', borderRadius: '8px' }}></div>
                        <div className="skeleton skeleton-button" style={{ width: '36px', height: '36px', borderRadius: '8px' }}></div>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container">
        <div className="legal-pages-header">
          <h1>Legal Pages</h1>
          <Link to="/sy-admin/legal-pages/new" className="btn btn-primary">
            Add New Page
          </Link>
        </div>

        {error && <div className="error-message">{error}</div>}

        {/* Filters */}
        <div className="legal-pages-filters">
          <input
            type="text"
            placeholder="Search pages..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="filter-select"
          >
            <option value="">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>

        {/* List */}
        {legalPages.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">📄</div>
            <h2>No legal pages found</h2>
            <p>No legal pages match your search criteria.</p>
            <Link to="/sy-admin/legal-pages/new" className="btn btn-primary" style={{ marginTop: '16px' }}>
              Add Your First Page
            </Link>
          </div>
        ) : (
          <div className="legal-pages-table-container">
            <table className="legal-pages-table">
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Slug</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th>Updated</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {legalPages.map((page) => {
                  const isTogglingStatus = togglingStatus[page._id];
                  return (
                    <tr key={page._id}>
                      <td>
                        <div className="legal-page-title-cell">
                          <span className="legal-page-title-text">{page.title}</span>
                        </div>
                      </td>
                      <td>
                        <div className="legal-page-slug-cell">
                          <code className="slug-text">/{page.slug}</code>
                        </div>
                      </td>
                      <td>
                        <div className="status-cell">
                          <label className="status-toggle-label">
                            <div className="toggle-switch">
                              <input
                                type="checkbox"
                                checked={page.status === 'active'}
                                onChange={() => handleToggleActive(page._id, page.status)}
                                disabled={isTogglingStatus}
                                className="toggle-input"
                              />
                              <span className={`toggle-slider ${isTogglingStatus ? 'loading' : ''}`}></span>
                            </div>
                            <span className={`status-text ${page.status === 'active' ? 'status-active-text' : 'status-inactive-text'}`}>
                              {page.status === 'active' ? 'Active' : 'Inactive'}
                            </span>
                          </label>
                        </div>
                      </td>
                      <td>
                        <div className="legal-page-created-cell">
                          <span>{formatDate(page.createdAt)}</span>
                        </div>
                      </td>
                      <td>
                        <div className="legal-page-updated-cell">
                          <span>{formatDate(page.updatedAt)}</span>
                        </div>
                      </td>
                      <td>
                        <div className="table-actions">
                          <button
                            onClick={() => handleViewPage(page.slug)}
                            className="btn btn-view btn-icon-only"
                            title="View Page"
                          >
                            <span className="btn-icon">👁️</span>
                          </button>
                          <button
                            onClick={() => navigate(`/sy-admin/legal-pages/edit/${page._id}`)}
                            className="btn btn-edit btn-icon-only"
                            title="Edit Page"
                          >
                            <span className="btn-icon">✏️</span>
                          </button>
                          <button
                            onClick={() => handleDelete(page._id)}
                            className="btn btn-delete btn-icon-only"
                            title="Delete Page"
                          >
                            <span className="btn-icon">🗑️</span>
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default LegalPages;
